import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import examMasterService from "../../services/examMasterService";

export const fetchExamCategories = createAsyncThunk(
  "examCategories/fetchExamCategories",
  async (_, { rejectWithValue }) => {
    try {
      return await examMasterService.getExamCategories();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch exam categories");
    }
  }
);

export const fetchActiveExamCategories = createAsyncThunk(
  "examCategories/fetchActiveExamCategories",
  async (_, { rejectWithValue }) => {
    try {
      return await examMasterService.getActiveExamCategories();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch active exam categories");
    }
  }
);

export const createExamCategory = createAsyncThunk(
  "examCategories/createExamCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const data = payload?.data || payload;
      const imageFile = payload?.imageFile || null;
      return await examMasterService.createExamCategory(data, imageFile);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create exam category");
    }
  }
);

export const updateExamCategory = createAsyncThunk(
  "examCategories/updateExamCategory",
  async ({ id, data, imageFile }, { rejectWithValue }) => {
    try {
      return await examMasterService.updateExamCategory(id, data, imageFile);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update exam category");
    }
  }
);

export const deleteExamCategory = createAsyncThunk(
  "examCategories/deleteExamCategory",
  async (id, { rejectWithValue }) => {
    try {
      await examMasterService.deleteExamCategory(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete exam category");
    }
  }
);

const examCategorySlice = createSlice({
  name: "examCategories",
  initialState: {
    examCategories: [],
    activeExamCategories: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchExamCategories.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchExamCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.examCategories = action.payload;
      })
      // Fetch Active
      .addCase(fetchActiveExamCategories.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchActiveExamCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeExamCategories = action.payload;
      })
      .addCase(fetchActiveExamCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchExamCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createExamCategory.fulfilled, (state, action) => {
        state.examCategories.unshift(action.payload);
        state.success = "Exam category created successfully";
      })
      // Update
      .addCase(updateExamCategory.fulfilled, (state, action) => {
        const index = state.examCategories.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.examCategories[index] = action.payload;
        }
        state.success = "Exam category updated successfully";
      })
      // Delete
      .addCase(deleteExamCategory.fulfilled, (state, action) => {
        state.examCategories = state.examCategories.filter(c => c.id !== action.payload);
        state.success = "Exam category deleted successfully";
      });
  },
});

export const { clearError, clearSuccess } = examCategorySlice.actions;
export default examCategorySlice.reducer;
